import type { Option } from 'fp-ts/Option'
import { none, some } from 'fp-ts/Option'

export interface ResourcePagination {
    pages: number
    recordsCount: number
    countIsLimited: boolean
    page: number
}

export interface ResourceLinks {
    pdf_download: string
}

export interface TextraPagination {
    pagination: ResourcePagination
}
export interface TextraMeta<T> {
    meta: T
}
export interface TextraLinks {
    links: ResourceLinks
}

export interface ResourceInit {
    status: 'init'
}

export interface ResourceSubmitted {
    status: 'submitted'
}

export type ResourceFulfilled<Tdata, Textra> = {
    status: 'fulfilled'
    data: Tdata
} & Textra

export interface ResourceFail<TdataFail> {
    status: 'fail'
    data: TdataFail
}

export interface ResourceRejected<TdataRejected> {
    status: 'rejected'
    data: TdataRejected
}

export type Resource<Tdata = unknown, Textra = unknown, TdataRejected = unknown, TdataFail = unknown> =
    | ResourceInit
    | ResourceSubmitted
    | ResourceFulfilled<Tdata, Textra>
    | ResourceRejected<TdataRejected>
    | ResourceFail<TdataFail>

export type ExtractFromResource<R extends Resource, state extends Resource['status']> = Extract<R, { status: state }>

export const resourceIsInit = <Tdata, Textra, TdataRejected, TdataFail>(
    resource: Resource<Tdata, Textra, TdataRejected, TdataFail>
): resource is ResourceFulfilled<Tdata, Textra> | ResourceFail<TdataFail> | ResourceRejected<TdataRejected> => {
    return resource.status === 'init'
}

export const resourceIsAcknowledged = <Tdata, Textra, TdataRejected, TdataFail>(
    resource: Resource<Tdata, Textra, TdataRejected, TdataFail>
): resource is ResourceFulfilled<Tdata, Textra> | ResourceFail<TdataFail> | ResourceRejected<TdataRejected> => {
    return resource.status === 'fail' || resource.status === 'fulfilled' || resource.status === 'rejected'
}

export const resourceIsFullfilled = <Tdata, Textra, TdataRejected, TdataFail>(
    resource: Resource<Tdata, Textra, TdataRejected, TdataFail>
): resource is ResourceFulfilled<Tdata, Textra> => {
    return resource.status === 'fulfilled'
}

export const resourceIsBadAcknowledged = <Tdata, Textra, TdataRejected, TdataFail>(
    resource: Resource<Tdata, Textra, TdataRejected, TdataFail>
): resource is ResourceFail<TdataFail> | ResourceRejected<TdataRejected> => {
    return resource.status === 'fail' || resource.status === 'rejected'
}

export const optFromResource = <Tdata, Tmeta, TdataFail, TdataRejected>(
    resource: Resource<Tdata, Tmeta, TdataFail, TdataRejected>
): Option<ResourceFulfilled<Tdata, Tmeta>> => {
    switch (resource.status) {
        case 'init':
        case 'fail':
        case 'rejected':
        case 'submitted':
            return none
        case 'fulfilled':
            return some(resource)
    }
}

export const resourceInit: ResourceInit = { status: 'init' }
